import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Portal from '../../Portal/Portal';
import Icon from '../Icon/Icon';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { LOCALES } from 'src/constants/main';
import theme from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import { throttle } from 'src/utils/perfomance';
import './style.scss';

interface IProps {
  open: boolean;
  text: string;
  textClassName?: string;
  buttonTitle?: string;
  buttonClassName?: string;
  buttonTitleClassName?: string;
  onClick: () => void;
  children?: ReactNode;
  minTop?: number;
  minBottom?: number;
  pollingFrequency?: number;
}

let hintBoxInterval: NodeJS.Timeout | null = null;

export const HintBox = ({ minTop = 0, minBottom = 0, pollingFrequency = 20, ...props }: IProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const [contentX, setContentX] = useState<number>(0);
  const [contentY, setContentY] = useState<number>(0);

  const timerStart = () => {
    if (!props.open || !contentRef) {
      if (hintBoxInterval) clearInterval(hintBoxInterval);
      return;
    }

    hintBoxInterval = setInterval(() => {
      const currentX: number | undefined = contentRef?.current?.getClientRects()[0]?.x;
      const currentY: number | undefined = contentRef?.current?.getClientRects()[0]?.y;

      if (currentX && contentX !== currentX) throttle(setContentX(currentX), pollingFrequency + 5);
      if (currentY && contentY !== currentY) throttle(setContentY(currentY), pollingFrequency + 5);
    }, pollingFrequency);
  };

  useEffect(() => {
    if (props.open && contentRef) timerStart();
    else if (hintBoxInterval) clearInterval(hintBoxInterval);

    return () => {
      if (hintBoxInterval) clearInterval(hintBoxInterval);
    };
  }, [props.open, contentRef]);

  const hintBoxPosition = useMemo((): React.CSSProperties => {
    if (!contentRef.current || !portalRef.current) return {};

    const { width: contentWidth } = contentRef.current.getClientRects()[0];
    const { width: portalWidth, height: portalHeight } = portalRef.current.getClientRects()[0];
    let top: string | number = contentY - 5;
    let left: string | number = 0;
    let bottom: string | number = 'initial';

    if (contentY < minTop + 5) {
      top = minTop;
    }
    if (contentY > window.innerHeight - (portalHeight + 5 + minBottom)) {
      top = 'initial';
      bottom = minBottom;
    }

    left = localeStore.language === LOCALES.AR ? contentX + contentWidth + 15 : contentX - portalWidth - 15;

    return { top, left, bottom };
  }, [contentX, contentY, minTop, minBottom]);

  return (
    <div className="hint-box__wrapper">
      {props.open ? (
        <Portal>
          <div ref={portalRef} className="hint-box" style={hintBoxPosition}>
            <p className={`hint-box__text letter-spacing-5 mb-2 ${props.textClassName || ''}`}>{localeStore.t(props.text)}</p>
            <button onClick={props.onClick} className={`hint-box__button ${props.buttonClassName || ''}`}>
              <span className={`hint-box__button-title letter-spacing-12 ${props.buttonTitleClassName || ''}`}>
                {localeStore.t(props.buttonTitle || 'got_it')}
              </span>
            </button>
            <Icon name={Icons.HintArrow} size={20} color={theme.colors.mainblue} className="hint-box__arrow" />
          </div>
        </Portal>
      ) : null}
      <div ref={contentRef} className="hint-box__ref-wrapper">
        {props.children}
      </div>
    </div>
  );
};
